import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";

interface CustomExercise {
  id: string;
  name: string;
  description?: string;
  category: string;
  muscle_groups: string[];
  equipment?: string;
  instructions?: string;
}

interface AddCustomExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExerciseAdded: (exercise: CustomExercise) => void;
}

const CATEGORIES = [
  "Chest", "Back", "Shoulders", "Arms", "Legs", "Core", "Cardio", "Full Body", "Other"
];

const MUSCLE_GROUPS = [
  "Chest", "Back", "Shoulders", "Biceps", "Triceps", "Forearms", 
  "Quadriceps", "Hamstrings", "Glutes", "Calves", "Abs", "Obliques", 
  "Traps", "Lats", "Delts", "Hip Flexors"
];

const EQUIPMENT_OPTIONS = [
  "Barbell", "Dumbbell", "Kettlebell", "Cable", "Machine", "Bodyweight", 
  "Resistance Band", "Medicine Ball", "TRX", "Plate", "Other", "None"
];

export const AddCustomExerciseModal = ({ isOpen, onClose, onExerciseAdded }: AddCustomExerciseModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    equipment: "",
    instructions: ""
  });
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>([]);
  const [muscleGroupInput, setMuscleGroupInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(t('components.addCustomExercise.validation.loginRequired'));
      return;
    }

    if (!formData.name.trim()) {
      toast.error(t('components.addCustomExercise.validation.nameRequired'));
      return;
    }

    if (!formData.category) {
      toast.error(t('components.addCustomExercise.validation.categoryRequired'));
      return;
    }

    if (selectedMuscleGroups.length === 0) {
      toast.error(t('components.addCustomExercise.validation.muscleGroupRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('custom_exercises')
        .insert({
          user_id: user.id,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          category: formData.category,
          muscle_groups: selectedMuscleGroups,
          equipment: formData.equipment || null,
          instructions: formData.instructions.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      const newExercise: CustomExercise = {
        id: data.id,
        name: data.name,
        description: data.description,
        category: data.category,
        muscle_groups: data.muscle_groups,
        equipment: data.equipment,
        instructions: data.instructions
      };

      onExerciseAdded(newExercise);
      toast.success(t('components.addCustomExercise.toast.created'));
      
      // Reset form
      setFormData({ name: "", description: "", category: "", equipment: "", instructions: "" });
      setSelectedMuscleGroups([]);
      setMuscleGroupInput("");
      onClose();

    } catch (error) {
      console.error('Error creating custom exercise:', error);
      toast.error(t('components.addCustomExercise.toast.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const addMuscleGroup = () => {
    const muscle = muscleGroupInput.trim();
    if (muscle && !selectedMuscleGroups.includes(muscle)) {
      setSelectedMuscleGroups([...selectedMuscleGroups, muscle]);
      setMuscleGroupInput("");
    }
  };

  const removeMuscleGroup = (muscle: string) => {
    setSelectedMuscleGroups(selectedMuscleGroups.filter(m => m !== muscle));
  };

  const handleMuscleGroupKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addMuscleGroup();
    }
  };

  const addPredefinedMuscleGroup = (muscle: string) => {
    if (!selectedMuscleGroups.includes(muscle)) {
      setSelectedMuscleGroups([...selectedMuscleGroups, muscle]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('components.addCustomExercise.title')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Exercise Name */}
          <div>
            <Label htmlFor="exercise-name">{t('components.addCustomExercise.exerciseNameLabel')}</Label>
            <Input
              id="exercise-name"
              placeholder={t('components.addCustomExercise.exerciseNamePlaceholder')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-2"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">{t('components.addCustomExercise.descriptionLabel')}</Label>
            <Textarea
              id="description"
              placeholder={t('components.addCustomExercise.descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-2"
              rows={3}
            />
          </div>

          {/* Category */}
          <div>
            <Label>{t('components.addCustomExercise.categoryLabel')}</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder={t('components.addCustomExercise.categoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Muscle Groups */}
          <div>
            <Label>{t('components.addCustomExercise.muscleGroupsLabel')}</Label>
            <div className="mt-2 space-y-3">
              {/* Quick add buttons */}
              <div className="flex flex-wrap gap-2">
                {MUSCLE_GROUPS.map((muscle) => (
                  <Button
                    key={muscle}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addPredefinedMuscleGroup(muscle)}
                    disabled={selectedMuscleGroups.includes(muscle)}
                    className="h-7 text-xs"
                  >
                    {muscle}
                  </Button>
                ))}
              </div>
              
              {/* Custom muscle group input */}
              <div className="flex gap-2">
                <Input
                  placeholder={t('components.addCustomExercise.customMuscleGroupPlaceholder')}
                  value={muscleGroupInput}
                  onChange={(e) => setMuscleGroupInput(e.target.value)}
                  onKeyDown={handleMuscleGroupKeyPress}
                  className="flex-1"
                />
                <Button type="button" onClick={addMuscleGroup} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Selected muscle groups */}
              {selectedMuscleGroups.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedMuscleGroups.map((muscle) => (
                    <Badge key={muscle} variant="secondary" className="flex items-center gap-1">
                      {muscle}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMuscleGroup(muscle)}
                        className="h-auto p-0 hover:bg-transparent"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Equipment */}
          <div>
            <Label>{t('components.addCustomExercise.equipmentLabel')}</Label>
            <Select value={formData.equipment} onValueChange={(value) => setFormData({ ...formData, equipment: value })}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder={t('components.addCustomExercise.equipmentPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {EQUIPMENT_OPTIONS.map((equipment) => (
                  <SelectItem key={equipment} value={equipment}>
                    {equipment}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Instructions */}
          <div>
            <Label htmlFor="instructions">{t('components.addCustomExercise.instructionsLabel')}</Label>
            <Textarea
              id="instructions"
              placeholder={t('components.addCustomExercise.instructionsPlaceholder')}
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              className="mt-2"
              rows={4}
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="bg-gradient-primary text-black"
          >
            {isSubmitting ? t('components.addCustomExercise.creating') : t('components.addCustomExercise.createExercise')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};